import React, { useEffect, useRef } from "react";
import { useVerifyToken } from "../hooks/useVerifyToken";

interface EmailVerificationHandlerProps {
  id: string;
  token: string;
  onVerificationComplete: (tempData: unknown) => void;
  onVerificationError: (error: string) => void;
}

const EmailVerificationHandler: React.FC<EmailVerificationHandlerProps> = ({
  id,
  token,
  onVerificationComplete,
  onVerificationError,
}) => {
  const {
    verifyToken,
    loading: tokenLoading,
    error: tokenError,
  } = useVerifyToken();

  // Use ref to prevent multiple verification attempts for the same token
  const verificationAttempted = useRef<string | null>(null);
  const currentTokenRef = useRef<string>(token);

  useEffect(() => {
    const performVerification = async () => {
      // CRITICAL FIX: Only prevent verification if it's the exact same token
      // This allows new tokens to be processed while preventing duplicate calls for the same token
      if (verificationAttempted.current === token) {
        console.log(
          "Email verification already attempted for this token, skipping...",
          {
            token: token.substring(0, 8) + "...",
            previousToken:
              verificationAttempted.current?.substring(0, 8) + "...",
          }
        );
        return;
      }

      // CRITICAL FIX: Clear any previous verification state for new tokens
      if (currentTokenRef.current !== token) {
        console.log(
          "New token detected, clearing previous verification state:",
          {
            oldToken: currentTokenRef.current?.substring(0, 8) + "...",
            newToken: token.substring(0, 8) + "...",
          }
        );
        verificationAttempted.current = null;
        currentTokenRef.current = token;
      }

      verificationAttempted.current = token;

      console.log("Starting email verification process...", {
        id: id,
        token: token.substring(0, 8) + "...",
        timestamp: new Date().toISOString(),
      });

      try {
        // CRITICAL FIX: Verify token now returns tempData directly from backend
        // No need for separate retrieveTempData API call
        console.log("Step 1: Verifying token and retrieving tempData...");
        const tokenResult = await verifyToken(token, id);

        if (!tokenResult.success) {
          const errorMsg = `Token verification failed: ${tokenResult.message}`;
          console.error(errorMsg);
          onVerificationError(errorMsg);
          return;
        }

        console.log("Step 1 completed: Token verified successfully");

        // Validate that tempData was returned
        if (!tokenResult.tempData) {
          const errorMsg =
            "Token verification succeeded but no tempData was returned";
          console.error(errorMsg);
          onVerificationError(errorMsg);
          return;
        }

        console.log(
          "Step 2 completed: Temporary data retrieved from verify response"
        );

        // Complete verification
        console.log("Email verification completed successfully!", {
          tempDataId: tokenResult.tempData.tempDataId,
          verificationId: tokenResult.tempData.verificationId,
          timestamp: new Date().toISOString(),
        });

        onVerificationComplete(tokenResult.tempData);
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Unknown verification error";
        console.error("Email verification process failed:", errorMsg);

        // CRITICAL FIX: Reset verification state on error to allow retry
        verificationAttempted.current = null;

        onVerificationError(errorMsg);
      }
    };

    // Start verification process
    performVerification();
  }, [id, token, verifyToken, onVerificationComplete, onVerificationError]);

  // Handle individual hook errors
  useEffect(() => {
    if (tokenError) {
      console.error("Token verification hook error:", tokenError);

      // CRITICAL FIX: Reset verification state on hook error to allow retry
      verificationAttempted.current = null;

      onVerificationError(`Token verification error: ${tokenError}`);
    }
  }, [tokenError, onVerificationError]);

  // Log loading state for debugging
  useEffect(() => {
    if (tokenLoading) {
      console.log("Token verification in progress...");
    }
  }, [tokenLoading]);

  // This component handles logic only and doesn't render any UI
  return null;
};

export default EmailVerificationHandler;
